import { Suspense, useContext, useEffect, useMemo } from "react";
import { usePaginationFragment, usePreloadedQuery, useQueryLoader } from "react-relay";
import { useDebounceFilters } from "@hooks/useDebounceFilters";
import { useTranslation } from "@i18n/hooks";
import { type filesTableV2_FilesListFragment$key } from "@relay/filesTableV2_FilesListFragment.graphql";
import { type filesTableV2_Query } from "@relay/filesTableV2_Query.graphql";
import { type FilesTableV2_Refetch } from "@relay/FilesTableV2_Refetch.graphql";
import { FilesTableUI } from "./files-table-ui.component";
import { FILES_FRAGMENT, QUERY } from "./files-table.graphql";
import {
	type FilesTableV2WithPreloadedQueryProps,
	type FilesTableV2Props,
} from "./files-table.types";
import { FileIsUsedInNodeAttachmentsDialog } from "../FileIsUsedInNodeAttachmentsDialog";
import { FileSearchContainer, type FilesFilters, FilesSearchContext } from "../FileSearchContainer";

const FilesTableComponent = ({
	selectionSettings,
	canUploadFiles,
	canDeleteFiles,
	queryRef,
}: FilesTableV2WithPreloadedQueryProps) => {
	const { t } = useTranslation("common");
	const { filters } = useContext(FilesSearchContext);

	const data = usePreloadedQuery<filesTableV2_Query>(QUERY, queryRef);

	const {
		data: { Admin },
		hasPrevious,
		hasNext,
		loadPrevious,
		loadNext,
		refetch,
	} = usePaginationFragment<FilesTableV2_Refetch, filesTableV2_FilesListFragment$key>(
		FILES_FRAGMENT,
		data,
	);

	const filtersWithDefault = useMemo(() => {
		const filtersCopy = { ...filters };
		filtersCopy.filterByFileTypes = filters.filterByFileTypes?.length
			? filters.filterByFileTypes
			: selectionSettings?.fileTypeOptions?.map((type) => type.value);
		return filtersCopy;
	}, [filters]);

	useDebounceFilters<FilesFilters>(filtersWithDefault, refetch);

	const files = Admin.Files.Files.edges;
	const connectionId = Admin.Files.Files.__id;

	return (
		<FilesTableUI
			files={files}
			hasPrevious={hasPrevious}
			hasNext={hasNext}
			loadPrevious={loadPrevious}
			loadNext={loadNext}
			onRefetch={() => refetch({}, { fetchPolicy: "network-only" })}
			selectionSettings={selectionSettings}
			canUploadFiles={canUploadFiles}
			canDeleteFiles={canDeleteFiles}
			uploadEndpoint="/api/upload"
			renderDeleteDialog={(fileId, onClose) => (
				<Suspense fallback={<div>{t("app.loading")}</div>}>
					<FileIsUsedInNodeAttachmentsDialog
						id={fileId}
						connection={connectionId}
						showDialog={true}
						setShowDialog={(v) => {
							if (!v) onClose();
						}}
					/>
				</Suspense>
			)}
		/>
	);
};

export const FilesTable = ({
	selectionSettings,
	filterByFileTypes,
	...props
}: FilesTableV2Props) => {
	const { t } = useTranslation("common");
	const [queryReference, loadQuery] = useQueryLoader<filesTableV2_Query>(QUERY);

	useEffect(() => {
		loadQuery(
			{
				filterByFileTypes:
					selectionSettings?.fileTypeOptions?.map((o) => o.value) ?? filterByFileTypes,
			},
			{ fetchPolicy: "network-only" },
		);
	}, [loadQuery]);

	return (
		<Suspense fallback={t("app.loading")}>
			{queryReference && (
				<FileSearchContainer>
					<FilesTableComponent
						{...props}
						selectionSettings={selectionSettings}
						filterByFileTypes={filterByFileTypes}
						queryRef={queryReference}
					/>
				</FileSearchContainer>
			)}
		</Suspense>
	);
};
